import React from 'react';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import DateRange from 'components/Form/DateRange';
import Textarea from 'components/Form/Textarea';
import LocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Text from 'components/Form/Text';
import { Dispatch } from 'redux';
import { LegalAssociationsOrganizationalItemModel } from 'forms/sections/legal/associations/organizationalItem/types';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonAddressBooks,
  CommonOnError,
  CommonOnUpdate,
  CommonRequired,
  CommonScrollIntoView,
} from '../../../../types/props';

interface Props
  extends Partial<LegalAssociationsOrganizationalItemModel>,
  ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  dispatch?: Dispatch
}

export default class OrganizationalItem extends ValidationElement<Props> {
  update = (queue: Partial<LegalAssociationsOrganizationalItemModel>) => {
    this.props.onUpdate({
      Organization: this.props.Organization,
      HasUSAddress: this.props.HasUSAddress,
      USAddress: this.props.USAddress,
      HasForeignAddress: this.props.HasForeignAddress,
      ForeignAddress: this.props.ForeignAddress,
      HasWebpage: this.props.HasWebpage,
      Webpage: this.props.Webpage,
      Dates: this.props.Dates,
      ReasonsForStaying: this.props.ReasonsForStaying,
      ReasonsForLeaving: this.props.ReasonsForLeaving,
      HeldPositions: this.props.HeldPositions,
      PositionsHeld: this.props.PositionsHeld,
      HasMadeContributions: this.props.HasMadeContributions,
      ContributionAmount: this.props.ContributionAmount,
      WasAwareOfViolence: this.props.WasAwareOfViolence,
      WasAwareOfViolenceExplaination: this.props.WasAwareOfViolenceExplaination,
      IntendedViolence: this.props.IntendedViolence,
      IntendedViolenceExplaination: this.props.IntendedViolenceExplaination,
      DidUseViolence: this.props.DidUseViolence,
      UseOfViolenceExplaination: this.props.UseOfViolenceExplaination,
      ReasonForJoining: this.props.ReasonForJoining,
      RoleInOrg: this.props.RoleInOrg,
      ...queue,
    });
  };

  updateOrganization = (
    values: LegalAssociationsOrganizationalItemModel['Organization']
  ) => {
    this.update({
      Organization: values,
    });
  };

  updateHasUSAddress = (values: LegalAssociationsOrganizationalItemModel['HasUSAddress']) => {
    this.update({
      HasUSAddress: values,
    });
  };

  updateForeignAddress = (values: LegalAssociationsOrganizationalItemModel['ForeignAddress']) => {
    this.update({
      ForeignAddress: values,
    });
  };

  updateHasForeignAddress = (values: LegalAssociationsOrganizationalItemModel['HasForeignAddress']) => {
    this.update({
      HasForeignAddress: values,
    });
  };

  updateUSAddress = (values: LegalAssociationsOrganizationalItemModel['USAddress']) => {
    this.update({
      USAddress: values,
    });
  };

  updateHasWebpage = (values: LegalAssociationsOrganizationalItemModel['HasWebpage']) => {
    this.update({
      HasWebpage: values,
    });
  };

  updateWebpage = (values: LegalAssociationsOrganizationalItemModel['Webpage']) => {
    this.update({
      Webpage: values,
    });
  };

  updateDates = (values: LegalAssociationsOrganizationalItemModel['Dates']) => {
    // TODO: clear updateReasonsForStaying if present is true and vice versa
    this.update({
      Dates: values,
    });
  };

  updateReasonsForStaying = (values: LegalAssociationsOrganizationalItemModel['ReasonsForStaying']) => {
    this.update({
      ReasonsForStaying: values,
    });
  };

  updateReasonsForLeaving = (values: LegalAssociationsOrganizationalItemModel['ReasonsForLeaving']) => {
    this.update({
      ReasonsForLeaving: values,
    });
  };

  updateHeldPositions = (values: LegalAssociationsOrganizationalItemModel['HeldPositions']) => {
    this.update({
      HeldPositions: values,
    });
  };

  updatePositionsHeld = (values: LegalAssociationsOrganizationalItemModel['PositionsHeld']) => {
    this.update({
      PositionsHeld: values,
    });
  };

  updateHasMadeContributions = (values: LegalAssociationsOrganizationalItemModel['HasMadeContributions']) => {
    this.update({
      HasMadeContributions: values,
    });
  };

  updateContributionAmount = (values: LegalAssociationsOrganizationalItemModel['ContributionAmount']) => {
    this.update({
      ContributionAmount: values,
    });
  };

  updateWasAwareOfViolence = (values: LegalAssociationsOrganizationalItemModel['WasAwareOfViolence']) => {
    this.update({
      WasAwareOfViolence: values,
    });
  };

  updateWasAwareOfViolenceExplaination = (values: LegalAssociationsOrganizationalItemModel['WasAwareOfViolenceExplaination']) => {
    this.update({
      WasAwareOfViolenceExplaination: values,
    });
  };

  updateIntendedViolence = (values: LegalAssociationsOrganizationalItemModel['IntendedViolence']) => {
    this.update({
      IntendedViolence: values,
    });
  };

  updateIntendedViolenceExplaination = (values: LegalAssociationsOrganizationalItemModel['IntendedViolenceExplaination']) => {
    this.update({
      IntendedViolenceExplaination: values,
    });
  };

  updateDidUseViolence = (values: LegalAssociationsOrganizationalItemModel['DidUseViolence']) => {
    this.update({
      DidUseViolence: values,
    });
  };

  updateUseOfViolenceExplaination = (values: LegalAssociationsOrganizationalItemModel['UseOfViolenceExplaination']) => {
    this.update({
      UseOfViolenceExplaination: values,
    });
  };

  updateReasonForJoining = (values: LegalAssociationsOrganizationalItemModel['ReasonForJoining']) => {
    this.update({
      ReasonForJoining: values,
    });
  };

  updateRoleInOrg = (values: LegalAssociationsOrganizationalItemModel['RoleInOrg']) => {
    this.update({
      RoleInOrg: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const {
      subSectionAssetPath,
      subSectionClassName,
      aware,
      intended,
      engaged,
    } = this.props;

    return (
      <div>
        <Field
          title={i18n.t(`${subSectionAssetPath}.organization`)}
          adjustFor="text"
          attribute={`${attribute}.Organization`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            name="Organization"
            attribute={`${attribute}.Organization`}
            {...this.props.Organization}
            onUpdate={this.updateOrganization}
            onError={this.props.onError}
            className={`${subSectionClassName}-organization`}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t(`${subSectionAssetPath}.usAddress`)}
          adjustFor="address"
          attribute={`${attribute}.HasUSAddress`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Branch
            name="hasUSAddress"
            className={`${subSectionClassName}-has-terrorist-hasUSAddress`}
            {...this.props.HasUSAddress}
            dnkLabel={i18n.t('legal.pvq.associations.label.idk')}
            warning
            onError={this.props.onError}
            required={this.props.required}
            onUpdate={this.updateHasUSAddress}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.HasUSAddress`}
            showDNK
          />
          <Show when={this.props.HasUSAddress?.value === 'Yes'}>
            <p>{i18n.t(`${subSectionAssetPath}.whatAddress`)}</p>
            <LocationUnknown
              name="USAddress"
              attribute={`${attribute}.USAddress`}
              {...this.props.USAddress}
              country="United States"
              onUpdate={this.updateUSAddress}
              onError={this.props.onError}
              className={`${subSectionClassName}-USAddress`}
              layout={LocationUnknown.US_ADDRESS}
              geocode
              addressBooks={this.props.addressBooks}
              addressBook="Organization"
              dispatch={this.props.dispatch}
              required={this.props.required}
            />
          </Show>
        </Field>
        <Field
          title={i18n.t(`${subSectionAssetPath}.foreignAddress`)}
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.HasForeignAddress`}
        >
          <Branch
            name="HasForeignAddress"
            className={`${subSectionClassName}-has-terrorist-HasForeignAddress`}
            {...this.props.HasForeignAddress}
            dnkLabel={i18n.t('legal.pvq.associations.label.idk')}
            warning
            onError={this.props.onError}
            required={this.props.required}
            onUpdate={this.updateHasForeignAddress}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.HasForeignAddress`}
            showDNK
          />
          <Show when={this.props.HasForeignAddress?.value === 'Yes'}>
            <p>{i18n.t(`${subSectionAssetPath}.whatAddress`)}</p>
            <LocationUnknown
              name="ForeignAddress"
              label={i18n.t(`${subSectionAssetPath}.whatAddress`)}
              attribute={`${attribute}.ForeignAddress`}
              {...this.props.ForeignAddress}
              onUpdate={this.updateForeignAddress}
              onError={this.props.onError}
              className={`${subSectionClassName}-ForeignAddress`}
              layout={LocationUnknown.STREET_CITY_COUNTRY}
              geocode
              addressBooks={this.props.addressBooks}
              addressBook="Organization"
              dispatch={this.props.dispatch}
              required={this.props.required}
            />
          </Show>
        </Field>
        <Field
          title={i18n.t(`${subSectionAssetPath}.hasWebpage`)}
          adjustFor="text"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.HasWebpage`}
        >
          <Branch
            name="has_webpage"
            className={`${subSectionClassName}-has-terrorist-hasWebpage`}
            {...this.props.HasWebpage}
            dnkLabel={i18n.t('legal.pvq.associations.label.idk')}
            showDNK
            warning
            onError={this.props.onError}
            required={this.props.required}
            onUpdate={this.updateHasWebpage}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.HasWebpage`}
          />
          <Show when={this.props.HasWebpage?.value === 'Yes'}>
            <Text
              name="Webpage"
              label={i18n.t(`${subSectionAssetPath}.webpage`)}
              attribute={`${attribute}.Webpage`}
              {...this.props.Webpage}
              onUpdate={this.updateWebpage}
              onError={this.props.onError}
              className={`${subSectionClassName}-webpage`}
              required={this.props.required}
            />
          </Show>
        </Field>

        <Field
          title={i18n.t(`${subSectionAssetPath}.dates`)}
          help={`${subSectionAssetPath}.help.dates`}
          adjustFor="daterange"
          attribute={`${attribute}.Dates`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            name="Dates"
            attribute={`${attribute}.Dates`}
            {...this.props.Dates}
            onUpdate={this.updateDates}
            onError={this.props.onError}
            minDateEqualTo
            className={`${subSectionClassName}-dates`}
            required={this.props.required}
            hideFromDay
            hideToDay
          />

          <Show when={this.props?.Dates?.present}>
            <Textarea
              name="ReasonsForStaying"
              label={i18n.t(`${subSectionAssetPath}.reasonsForStaying`)}
              attribute={`${attribute}.ReasonsForStaying`}
              {...this.props.ReasonsForStaying}
              onUpdate={this.updateReasonsForStaying}
              onError={this.props.onError}
              className={`${subSectionClassName}-reasonsForStaying`}
              required={this.props.required}
            />
          </Show>
          <Show when={!this.props?.Dates?.present}>
            <Textarea
              name="reasonsForLeaving"
              label={i18n.t(`${subSectionAssetPath}.reasonsForLeaving`)}
              attribute={`${attribute}.ReasonsForLeaving`}
              {...this.props.ReasonsForLeaving}
              onUpdate={this.updateReasonsForLeaving}
              onError={this.props.onError}
              className={`${subSectionClassName}-reasonsForLeaving`}
              required={this.props.required}
            />
          </Show>

        </Field>
        <Field
          title={i18n.t(`${subSectionAssetPath}.heldPositions`)}
          adjustFor="text"
          attribute={`${attribute}.HeldPositions`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Branch
            name="heldPositions"
            className={`${subSectionClassName}-has-terrorist-heldPositions`}
            {...this.props.HeldPositions}
            warning
            onError={this.props.onError}
            required={this.props.required}
            onUpdate={this.updateHeldPositions}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.HeldPositions`}
          />
          <Show when={this.props.HeldPositions?.value === 'Yes'}>
            <Text
              name="PositionsHeld"
              label={i18n.t(`${subSectionAssetPath}.positionsHeld`)}
              attribute={`${attribute}.PositionsHeld`}
              {...this.props.PositionsHeld}
              onUpdate={this.updatePositionsHeld}
              onError={this.props.onError}
              className={`${subSectionClassName}-positionsHeld`}
              required={this.props.required}
            />
          </Show>
        </Field>
        <Field
          title={i18n.t(`${subSectionAssetPath}.hasMadeContributions`)}
          adjustFor="text"
          attribute={`${attribute}.HasMadeContributions`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Branch
            name="hasMadeContributions"
            className={`${subSectionClassName}-has-terrorist-hasMadeContributions`}
            {...this.props.HasMadeContributions}
            warning
            onError={this.props.onError}
            required={this.props.required}
            onUpdate={this.updateHasMadeContributions}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.HasMadeContributions`}
          />
          <Show when={this.props.HasMadeContributions?.value === 'Yes'}>
            <Text
              name="contributionAmount"
              label={i18n.t(`${subSectionAssetPath}.contributionAmount`)}
              attribute={`${attribute}.ContributionAmount`}
              {...this.props.ContributionAmount}
              onUpdate={this.updateContributionAmount}
              onError={this.props.onError}
              className={`${subSectionClassName}-contributionAmount`}
              required={this.props.required}
            />
          </Show>
        </Field>
        {aware && (
          <Field
            title={i18n.t(`${subSectionAssetPath}.awareOfViolence`)}
            adjustFor="text"
            attribute={`${attribute}.WasAwareOfViolence`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Branch
              name="wasAwareOfViolence"
              className={`${subSectionClassName}-has-terrorist-wasAwareOfViolence`}
              {...this.props.WasAwareOfViolence}
              warning
              onError={this.props.onError}
              required={this.props.required}
              onUpdate={this.updateWasAwareOfViolence}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.WasAwareOfViolence`}
            />
            <Show when={this.props.WasAwareOfViolence?.value === 'Yes'}>
              <Textarea
                name="wasAwareOfViolenceExplaination"
                label={i18n.t(`${subSectionAssetPath}.pleaseExplain`)}
                attribute={`${attribute}.WasAwareOfViolenceExplaination`}
                {...this.props.WasAwareOfViolenceExplaination}
                onUpdate={this.updateWasAwareOfViolenceExplaination}
                onError={this.props.onError}
                className={`${subSectionClassName}-wasAwareOfViolenceExplaination`}
                required={this.props.required}
              />
            </Show>
          </Field>
        )}
        {intended && (
          <Field
            title={i18n.t(`${subSectionAssetPath}.intendedViolence`)}
            adjustFor="text"
            attribute={`${attribute}.IntendedViolence`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Branch
              name="intendedViolence"
              className={`${subSectionClassName}-has-terrorist-intendedViolence`}
              {...this.props.IntendedViolence}
              warning
              onError={this.props.onError}
              required={this.props.required}
              onUpdate={this.updateIntendedViolence}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.IntendedViolence`}
            />
            <Show when={this.props.IntendedViolence?.value === 'Yes'}>
              <Textarea
                name="intendedViolenceExplaination"
                label={i18n.t(`${subSectionAssetPath}.pleaseExplain`)}
                attribute={`${attribute}.IntendedViolenceExplaination`}
                {...this.props.IntendedViolenceExplaination}
                onUpdate={this.updateIntendedViolenceExplaination}
                onError={this.props.onError}
                className={`${subSectionClassName}-intendedViolenceExplaination`}
                required={this.props.required}
              />
            </Show>
          </Field>
        )}
        {engaged && (
          <Field
            title={i18n.t(`${subSectionAssetPath}.useOfViolence`)}
            adjustFor="text"
            attribute={`${attribute}.DidUseViolence`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Branch
              name="useOfViolence"
              className={`${subSectionClassName}-has-terrorist-useOfViolence`}
              {...this.props.DidUseViolence}
              warning
              onError={this.props.onError}
              required={this.props.required}
              onUpdate={this.updateDidUseViolence}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.DidUseViolence`}
            />
            <Show when={this.props.DidUseViolence?.value === 'Yes'}>
              <Textarea
                name="useOfViolenceExplaination"
                label={i18n.t(`${subSectionAssetPath}.pleaseExplain`)}
                attribute={`${attribute}.UseOfViolenceExplaination`}
                {...this.props.UseOfViolenceExplaination}
                onUpdate={this.updateUseOfViolenceExplaination}
                onError={this.props.onError}
                className={`${subSectionClassName}-useOfViolenceExplaination`}
                required={this.props.required}
              />
            </Show>
          </Field>
        )}
        <Field
          title={i18n.t(`${subSectionAssetPath}.reasonForJoining`)}
          adjustFor="textarea"
          attribute={`${attribute}.ReasonForJoining`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="reasonForJoining"
            attribute={`${attribute}.ReasonForJoining`}
            {...this.props.ReasonForJoining}
            onUpdate={this.updateReasonForJoining}
            onError={this.props.onError}
            className={`${subSectionClassName}-reasonForJoining`}
            required={this.props.required}
          />
        </Field>
        <Field
          title={i18n.t(`${subSectionAssetPath}.roleInOrg`)}
          adjustFor="textarea"
          attribute={`${attribute}.RoleInOrg`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="roleInOrg"
            attribute={`${attribute}.RoleInOrg`}
            {...this.props.RoleInOrg}
            onUpdate={this.updateRoleInOrg}
            onError={this.props.onError}
            className={`${subSectionClassName}-roleInOrg`}
            required={this.props.required}
          />
        </Field>
      </div>
    );
  }
}

OrganizationalItem.defaultProps = {
  Organization: {},
  HasUSAddress: {},
  USAddress: {},
  HasForeignAddress: {},
  ForeignAddress: {},
  HasWebpage: {},
  Webpage: {},
  Dates: {},
  ReasonsForStaying: {},
  ReasonsForLeaving: {},
  HeldPositions: {},
  PositionsHeld: {},
  HasMadeContributions: {},
  ContributionAmount: {},
  WasAwareOfViolence: {},
  WasAwareOfViolenceExplaination: {},
  IntendedViolence: {},
  IntendedViolenceExplaination: {},
  DidUseViolence: {},
  UseOfViolenceExplaination: {},
  ReasonForJoining: {},
  RoleInOrg: {},
  subSectionAssetPath: 'error no asset path',
  subSectionClassName: 'error no className path',
  aware: false,
  intended: false,
  engaged: false,
  onUpdate: () => { },
  onError: (value, arr) => arr,
};
